import { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { getStoredUser } from '../../hooks/useAuth';

export default function VacancySelectionPage() {
  const navigate = useNavigate();
  const user = getStoredUser();

  useEffect(() => {
    if (!user) navigate('/', { replace: true });
  }, [user, navigate]);

  const handleLogout = () => {
    localStorage.removeItem('portal_auth_user');
    navigate('/', { replace: true });
  };

  return (
    <div
      className="min-h-screen bg-[#0d0f18] flex flex-col relative overflow-hidden"
      style={{ fontFamily: "'Outfit', sans-serif" }}
    >
      {/* Background decorative glows */}
      <div className="pointer-events-none absolute inset-0 overflow-hidden">
        <div
          className="absolute -top-32 -left-32 w-[480px] h-[480px] rounded-full opacity-10"
          style={{ background: 'radial-gradient(circle, #4ade80 0%, transparent 70%)' }}
        />
        <div
          className="absolute bottom-0 right-0 w-[400px] h-[400px] rounded-full opacity-8"
          style={{ background: 'radial-gradient(circle, #4ade80 0%, transparent 70%)' }}
        />
        <div
          className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[700px] h-[300px] rounded-full opacity-5"
          style={{ background: 'radial-gradient(ellipse, #4ade80 0%, transparent 70%)' }}
        />
      </div>
      <link
        href="https://fonts.googleapis.com/css2?family=Outfit:wght@400;500;600;700&display=swap"
        rel="stylesheet"
      />

      {/* Top bar */}
      <header className="flex items-center justify-between px-8 py-4 border-b border-[#1e2130] relative z-10">
        <div className="absolute bottom-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-emerald-500/30 to-transparent" />
        <div className="flex items-center gap-4">
          <img
            src="https://storage.readdy-site.link/project_files/2bf8aaf3-0f30-40d5-be17-1c495b690c02/c01c7bf6-da01-4692-a287-7cfb8ff624a9_lTZGkXUbeeGGTSg_1769785028.png?v=61d175ca0083da25e9bd41f5ab3012cf"
            alt="Логотип"
            className="h-10 object-contain"
          />
          <div className="hidden sm:flex flex-col leading-tight">
            <span className="text-[11px] text-slate-500 tracking-widest uppercase">Корпоративный портал</span>
            <span
              className="text-sm font-bold tracking-[0.14em] uppercase"
              style={{ color: '#4ade80', textShadow: '0 0 12px rgba(74,222,128,0.3)', fontFamily: "'Outfit', sans-serif" }}
            >
              «ПЛАТФОРМА РЕШЕНИЙ»
            </span>
          </div>
        </div>
        <div className="flex items-center gap-4">
          <span className="text-sm text-slate-400">
            {user?.name}
          </span>
          <button
            onClick={handleLogout}
            className="flex items-center gap-2 text-sm text-slate-500 hover:text-slate-300 transition-colors cursor-pointer whitespace-nowrap"
          >
            <span className="w-4 h-4 flex items-center justify-center">
              <i className="ri-logout-box-r-line" />
            </span>
            Выйти
          </button>
        </div>
      </header>

      {/* Main content */}
      <main className="flex-1 flex flex-col items-center justify-center px-6 py-16 relative z-10">
        <div className="text-center mb-14">
          <span className="inline-block text-xs font-semibold text-emerald-500 bg-emerald-500/10 border border-emerald-500/20 rounded-full px-4 py-1.5 mb-4">
            Выбор направления
          </span>
          <h1 className="text-4xl font-bold text-white mb-3">
            На какой позиции вы работаете?
          </h1>
          <p className="text-slate-500 text-base">
            Выберите вашу роль, чтобы перейти в личный кабинет
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 w-full max-w-3xl">
          {/* HR Manager card */}
          <div
            onClick={() => navigate('/hr-manager')}
            className="group relative bg-[#13161f] border border-[#1e2130] hover:border-amber-500/40 rounded-2xl p-10 flex flex-col items-center text-center cursor-pointer transition-all duration-300 hover:-translate-y-2"
          >
            <div
              className="w-20 h-20 flex items-center justify-center rounded-2xl mb-6 transition-all duration-300 group-hover:scale-110"
              style={{ background: 'linear-gradient(135deg, #f59e0b20, #f59e0b40)' }}
            >
              <i className="ri-user-star-line text-4xl text-amber-400" />
            </div>
            <h2 className="text-2xl font-bold text-white mb-3">HR Менеджер</h2>
            <p className="text-slate-500 text-sm leading-relaxed mb-8">
              Выгрузка и обработка откликов, управление собственными верификациями, согласование объявлений.
            </p>
            <button className="mt-auto w-full border border-[#2a2d3e] hover:border-amber-500/50 text-slate-300 hover:text-white rounded-xl py-3 text-sm font-medium transition-all duration-200 cursor-pointer whitespace-nowrap">
              Войти как HR менеджер
            </button>
          </div>

          {/* Chat Operator card */}
          <div
            onClick={() => navigate('/chat-operator')}
            className="group relative bg-[#13161f] border border-emerald-500/30 hover:border-emerald-500/60 rounded-2xl p-10 flex flex-col items-center text-center cursor-pointer transition-all duration-300 hover:-translate-y-2"
          >
            <div className="absolute top-4 right-4">
              <span className="text-xs text-emerald-400 bg-emerald-500/10 border border-emerald-500/20 rounded-full px-2.5 py-1">
                Активно
              </span>
            </div>
            <div
              className="w-20 h-20 flex items-center justify-center rounded-2xl mb-6 transition-all duration-300 group-hover:scale-110"
              style={{ background: 'linear-gradient(135deg, #10b98120, #10b98140)' }}
            >
              <i className="ri-message-3-line text-4xl text-emerald-400" />
            </div>
            <h2 className="text-2xl font-bold text-white mb-3">Оператор чата</h2>
            <p className="text-slate-500 text-sm leading-relaxed mb-8">
              Поддержка клиентов в онлайн-чате, решение вопросов, обработка обращений и консультации.
            </p>
            <button className="mt-auto w-full bg-emerald-500 hover:bg-emerald-400 text-white rounded-xl py-3 text-sm font-semibold transition-all duration-200 cursor-pointer whitespace-nowrap">
              Войти в кабинет
            </button>
          </div>
        </div>
      </main>

      {/* Footer branding */}
      <footer className="py-5 border-t border-[#1e2130] flex items-center justify-center gap-3 relative z-10">
        <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-emerald-500/20 to-transparent" />
        <span className="text-xs text-slate-700 tracking-widest uppercase">© 2026</span>
        <span className="text-slate-700">·</span>
        <span
          className="text-xs font-semibold tracking-[0.12em] uppercase"
          style={{ color: '#4ade8060' }}
        >
          «ПЛАТФОРМА РЕШЕНИЙ»
        </span>
        <span className="text-slate-700">·</span>
        <span className="text-xs text-slate-700 tracking-widest">v1.0.0</span>
      </footer>
    </div>
  );
}
